import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import styles from './edit-profile.module.scss'
import logo from '../../assets/img/brand/explore-w-logo-blue-large@2x.png'
import anchor from '../../assets/img/Explore-w-Imray-user-image-default@2x.jpg'
import { AppContext } from '../../context'
import LoadingFullScreen from '../../components/common/LoadingFullScreen'
import { Form, InputGroup } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { API } from '../../api'
import CustomModal from '../../components/ui/CustomModal'

const EditProfile = () => {
  const { userData, setUserData } = useContext(AppContext)
  const inputRef = useRef<HTMLInputElement>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const [uploadedImage, setUploadedImage] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [show, setShow] = useState(false)

  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      bio: '',
      home: '',
      boatName: '',
      boatType: '',
      boatYear: null,
      boatDraft: null,
      boatLength: null,
      boatModel: ''
    }
  })

  useEffect(() => {
    if (userData?.first_name) form.setValue('firstName', userData?.first_name)
    if (userData?.last_name) form.setValue('lastName', userData?.last_name)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData])

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
    reset
  } = form

  const email = userData?.email
  const fullName = `${userData?.first_name} ${userData?.last_name}`

  const updateProfileInfo = async (
    values: {
      firstName: string
      lastName: string
      bio: string
      home: string
      boatName: string
      boatType: string
      boatYear: null | number
      boatDraft: null | number
      boatLength: null | number
      boatModel: string
    },
    imageKey?: string
  ) =>
    await API.post('/profile', {
      first_name: values.firstName,
      last_name: values.lastName,
      profile_bio: values.bio,
      profile_home_port: values.home,
      profile_boat_name: values.boatName,
      profile_boat_type: values.boatType,
      profile_boat_model: values.boatModel,
      profile_boat_year: values.boatYear,
      profile_boat_length: values.boatLength,
      profile_boat_draft: values.boatDraft,
      photo: imageKey
    })

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (!inputRef || !inputRef.current) return

    inputRef.current.click()
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return

    const file = files[0]

    try {
      const reader = new FileReader()
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          setUploadedImage(reader.result)
        } else {
          new Error('File reading resulted in a non-string result.')
        }
      }
      reader.onerror = () => new Error('Error reading file.')

      reader.readAsDataURL(file as Blob) // Convert file to Base64
    } catch (e) {
      console.log(e, '- Error when uploading image.')
    }
  }

  const onSubmit = useCallback(async () => {
    const values = getValues()
    setIsLoading(true)
    if (uploadedImage) {
      try {
        const imageRes = await API.post('/upload-image', {
          image_binary: uploadedImage
        })

        const imageKey = imageRes.data.message.key

        updateProfileInfo(values, imageKey).then(async () => {
          const getUserDataRes = await API.get('/profile')
          setUserData(getUserDataRes.data.message)
          setIsLoading(false)
        })
      } catch (e) {
        reset()
        setIsLoading(false)
        console.log(e, '- Error saving profile details.')
      }
    } else {
      try {
        updateProfileInfo(values).then(async () => {
          const getUserDataRes = await API.get('/profile')
          setUserData(getUserDataRes.data.message)
          setIsLoading(false)
        })
      } catch (e) {
        reset()
        setIsLoading(false)
        console.log(e, '- Error saving profile details.')
      }
    }
  }, [uploadedImage, getValues()])

  if (!userData || isLoading) return <LoadingFullScreen />

  return (
    <>
      <CustomModal
        onConfirm={() => {
          formRef?.current?.requestSubmit()
          setShow(false)
        }}
        title="Upadting profile..."
        description="Are you sure you want to update your profile?"
        show={show}
        setShow={setShow}
      />
      <div className={styles.container}>
        <div className={styles.banner}>
          <img src={logo} alt="img" />
        </div>
        <div className={styles.wrapper}>
          <a href="/" className={`btn-pill ${styles['button-back']}`}>
            <span className="material-icons">chevron_left</span>
            Back to explore with Imray
          </a>
          <div>
            <h1 className={styles.heading1}>Your Profile</h1>
            <p className={`${styles.description} ${styles.underline}`}>
              Your profile Here is the information related to your public
              Explore with Imray social profile. You can modify your personal
              and boat information anytime. Your unique user name is{' '}
              <strong>{email}</strong>
            </p>
          </div>
          <div>
            <h2 className={styles.heading2}>About You</h2>
            <p className={styles.description}>
              Your profile image, bio and home (port) location helps your
              friends and other social users find you and follow your journey
              through Explore with Imray.
            </p>
            <div
              className={`${styles['space-between']} ${styles['upload-image-container']}`}
            >
              <div className={styles['space-between']}>
                <img
                  style={{
                    marginRight: '1rem',
                    borderRadius: uploadedImage ? '100%' : 0
                  }}
                  src={uploadedImage ?? anchor}
                  alt="Profile picture"
                  className={styles['profile-image']}
                />
                <h3 className={styles['full-name']}>{fullName}</h3>
              </div>
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                id="contained-button-file"
                onChange={handleFileUpload}
              />
              <label htmlFor="contained-button-file">
                <button
                  onClick={handleButtonClick}
                  className="btn btn-secondary"
                >
                  Edit profile image
                </button>
              </label>
            </div>
            <form ref={formRef} className={styles['edit-profile-grid']}>
              <InputGroup className="flex flex-column w-100">
                <Form.Label className="p-0 m-0" htmlFor="firstName" column="sm">
                  First Name*
                </Form.Label>
                <Form.Control
                  id="firstName"
                  {...register('firstName', { required: true, minLength: 1 })}
                  className="rounded-lg w-100"
                  type="text"
                  aria-label="firstName"
                  aria-describedby="basic-addon1"
                />
                {errors.firstName && (
                  <p style={{ fontSize: 12 }} className="error-message">
                    First Name is required.
                  </p>
                )}
              </InputGroup>
              <InputGroup className="flex flex-column w-100">
                <Form.Label className="p-0 m-0" htmlFor="lastName" column="sm">
                  Last Name*
                </Form.Label>
                <Form.Control
                  {...register('lastName', { required: true, minLength: 1 })}
                  id="lastName"
                  className="rounded-lg w-100"
                  type="text"
                  aria-label="lastName"
                  aria-describedby="basic-addon2"
                />
                {errors.lastName && (
                  <p style={{ fontSize: 12 }} className="error-message">
                    Last Name is required.
                  </p>
                )}
              </InputGroup>
              <InputGroup className="flex flex-column w-100">
                <Form.Label className="p-0 m-0" htmlFor="bio" column="sm">
                  Bio
                </Form.Label>
                <Form.Control
                  {...register('bio', { required: false, minLength: 1 })}
                  id="bio"
                  className="rounded-lg w-100"
                  type="text"
                  aria-label="bio"
                  aria-describedby="basic-addon3"
                />
              </InputGroup>
              <InputGroup className="flex flex-column w-100">
                <Form.Label className="p-0 m-0" htmlFor="home" column="sm">
                  Home (port) location
                </Form.Label>
                <Form.Control
                  {...register('home', { required: false, minLength: 1 })}
                  id="home"
                  className="rounded-lg w-100"
                  type="text"
                  aria-label="home"
                  aria-describedby="basic-addon4"
                />
              </InputGroup>
            </form>

            <p className={`${styles['required-message']} ${styles.underline}`}>
              * Indicates a required field. Using an image of yourself along
              with your real first and last name is optional.
            </p>

            <h2 className={styles.heading2}>About Your Boat</h2>
            <p className={styles.description}>
              Your profile image, bio and home (port) location helps your
              friends and other social users find you and follow your journey
              through Explore with Imray.
            </p>

            <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
              <div className={styles['edit-profile-grid']}>
                <InputGroup className="flex flex-column w-100">
                  <Form.Label
                    className="p-0 m-0"
                    htmlFor="boatName"
                    column="sm"
                  >
                    Boat Name
                  </Form.Label>
                  <Form.Control
                    id="boatName"
                    {...register('boatName', { required: false, minLength: 1 })}
                    className="rounded-lg w-100"
                    type="text"
                    aria-label="BoatName"
                    aria-describedby="basic-addon1"
                  />
                </InputGroup>

                <InputGroup className="flex flex-column w-100">
                  <Form.Label
                    className="p-0 m-0"
                    htmlFor="boatType"
                    column="sm"
                  >
                    Boat Type
                  </Form.Label>
                  <Form.Control
                    {...register('boatType', { required: false, minLength: 1 })}
                    id="boatType"
                    className="rounded-lg w-100"
                    type="text"
                    aria-label="boatType"
                    aria-describedby="basic-addon2"
                  />
                </InputGroup>
                <InputGroup className="flex flex-column w-100">
                  <div className={styles['edit-profile-grid']}>
                    <div className={styles.col}>
                      <Form.Label
                        className="p-0 m-0"
                        htmlFor="boatModel"
                        column="sm"
                      >
                        Boat Model
                      </Form.Label>
                      <Form.Control
                        id="boatName"
                        {...register('boatModel', {
                          required: false,
                          minLength: 1
                        })}
                        className="rounded-lg w-100"
                        type="text"
                        aria-label="boatModel"
                        aria-describedby="basic-addon1"
                      />
                    </div>
                    <div className={styles.col}>
                      <Form.Label
                        className="p-0 m-0"
                        htmlFor="boatYear"
                        column="sm"
                      >
                        Boat Year
                      </Form.Label>
                      <Form.Control
                        id="boatName"
                        {...register('boatYear', {
                          required: false,
                          valueAsNumber: true,
                          minLength: 1
                        })}
                        className="rounded-lg w-100"
                        type="number"
                        aria-label="boatYear"
                        aria-describedby="basic-addon1"
                      />
                    </div>
                  </div>
                </InputGroup>
                <InputGroup className="flex flex-column w-100">
                  <div className={styles['edit-profile-grid']}>
                    <div className={styles.col}>
                      <Form.Label
                        className="p-0 m-0"
                        htmlFor="boatLength"
                        column="sm"
                      >
                        Boat Length
                      </Form.Label>
                      <Form.Control
                        id="boatName"
                        {...register('boatLength', {
                          required: false,
                          valueAsNumber: true,
                          minLength: 1
                        })}
                        className="rounded-lg w-100"
                        type="number"
                        aria-label="boatLength"
                        aria-describedby="basic-addon1"
                      />
                    </div>
                    <div className={styles.col}>
                      <Form.Label
                        className="p-0 m-0"
                        htmlFor="boatDraft"
                        column="sm"
                      >
                        Boat Draft
                      </Form.Label>
                      <Form.Control
                        id="boatName"
                        {...register('boatDraft', {
                          required: false,
                          valueAsNumber: true,
                          minLength: 1
                        })}
                        className="rounded-lg w-100"
                        type="number"
                        aria-label="boatDraft"
                        aria-describedby="basic-addon1"
                      />
                    </div>
                  </div>
                </InputGroup>
              </div>
              <p
                className={`${styles['required-message']} ${styles.underline}`}
              >
                This section is optional and won't be displayed in your profile
                unless filled in.
              </p>
              <div className={styles['community-standards']}>
                <p>
                  <h1>Our Community Standards</h1>
                  We have built Explore with Imray to provide a space for social
                  interactions around sailing activities. Our community
                  standards are a roadmap for contributing to this diverse,
                  globe-trotting community.
                </p>
                <ul>
                  <li>
                    <p>
                      <strong>Be Kind</strong>
                      <br />
                      Treat others with respect and kindness on our digital
                      platform. Don&#39;t use hate speech or offensive language,
                      or post anything that may intimidate, exclude or silence
                      others on the platform.
                    </p>
                  </li>
                  <li>
                    <p>
                      <strong>Be Creative</strong>
                      <br />
                      Create your own amazing content and tell the world of your
                      adventures. If you copy someone else&#39;s content it may
                      be taken down in addition to other further consequence
                    </p>
                  </li>
                  <li>
                    <p>
                      <strong>Be Responsible</strong>
                      <br />
                      Make sure that you are being responsible towards rules,
                      laws and the environment of the places you visit and post
                      about. Don&#39;t overstep the boundaries in search of a
                      good story to share. Obey the laws and rules of places you
                      visit, and respect the environment you are in. Remember
                      that others might be inspired by your stories. Try to only
                      include information that you know to be correct.
                    </p>
                  </li>
                  <li>
                    <p>
                      <strong>Be Yourself</strong>
                      <br />
                      Tell your stories. Don&#39;t impersonate other
                      individuals, groups or organisations. Don&#39;t spam other
                      users or locations on the platform
                    </p>
                  </li>
                </ul>
                <p>
                  Please be aware that in the initial iteration of the Explore
                  with Imray app, the social features will primarily include the
                  ability to add locations and provide feedback. In subsequent
                  iterations, we plan to introduce a comprehensive social hub
                  where users can create and engage with a vibrant community. We
                  appreciate your participation in our journey as we continue to
                  enhance the app&#39;s social features.
                  <br /> Thanks,
                  <br /> Explore with Imray team.
                </p>
              </div>
              <p className={styles.terms}>
                By creating your Explore with Imray profile you are agreeing to
                our terms of use and with our community standards policies.
                Explore with Imray reserves the right to remove social user
                accounts and social posts that violate our terms of use or do
                not share our values without prior notice. For more information
                please see our{' '}
                <a
                  className={styles.anchor}
                  href="https://www.explorewithimray.com/community-standards"
                >
                  Community Standards
                </a>
                , our{' '}
                <a
                  className={styles.anchor}
                  href="https://imray.com/digital/explore"
                >
                  privacy policy
                </a>{' '}
                and our{' '}
                <a
                  className={styles.anchor}
                  href="https://imray.com/digital/explore"
                >
                  terms of use
                </a>
                .
              </p>
              <div
                className={`${styles['buttons-container']} ${styles.underline}`}
              >
                <button
                  onClick={e => {
                    e.preventDefault()
                    setShow(true)
                  }}
                  type="submit"
                  style={{ marginRight: '10px' }}
                  className="btn btn-primary"
                >
                  Update Profile
                </button>
                <button
                  onClick={() => window.location.replace('/')}
                  className="btn btn-secondary"
                >
                  Cancel Changes
                </button>
              </div>
            </form>
          </div>

          <div className={styles.footer}>
            <span style={{ padding: '5px' }}>
              ©2024 Imray, Laurie, Norie and Wilson Ltd
            </span>
            <div className={styles.privacy}>
              <a
                style={{ padding: '5px', fontSize: '14px' }}
                className={styles.anchor}
                href="https://imray.com/digital/explore"
              >
                Privacy Policy
              </a>
              <a
                style={{ padding: '5px', fontSize: '14px' }}
                className={styles.anchor}
                href="https://imray.com/digital/explore"
              >
                Terms of Use
              </a>
              <a
                style={{ padding: '5px', fontSize: '14px' }}
                className={styles.anchor}
                href="https://share.hsforms.com/1boX2mm3UQ9WfJQI8davdLw4udgz"
              >
                Feedback and Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default EditProfile
